import { useState } from "react";
import Toggle from "@/components/base/Toggle";
import { carServices, vehicleTypes, carPriceGrid, carOptions } from "@/mocks/services";
import type { PriceRow } from "@/mocks/services";

export default function CarWashTab() {
  const [services, setServices] = useState(carServices);
  const [priceGrid, setPriceGrid] = useState<PriceRow[]>(carPriceGrid);
  const [options, setOptions] = useState(carOptions);
  const [saved, setSaved] = useState(false);

  // Add service modal state
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newDuration, setNewDuration] = useState("");
  const [formError, setFormError] = useState("");

  // Add option modal state
  const [addOptionOpen, setAddOptionOpen] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [newOptionPrice, setNewOptionPrice] = useState("");
  const [optionError, setOptionError] = useState("");

  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const toggleService = (id: number) => {
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
    flash();
  };

  const setPrice = (rowIdx: number, colIdx: number, val: string) => {
    setPriceGrid((prev) =>
      prev.map((row, ri) =>
        ri === rowIdx
          ? { ...row, prices: row.prices.map((p, ci) => (ci === colIdx ? val : p)) }
          : row,
      ),
    );
    flash();
  };

  const toggleOption = (id: number) => {
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o)));
    flash();
  };

  const openAddService = () => {
    setNewName("");
    setNewDesc("");
    setNewDuration("");
    setFormError("");
    setAddServiceOpen(true);
  };

  const submitService = () => {
    if (!newName.trim()) {
      setFormError("اكتب اسم الخدمة أولاً");
      return;
    }
    const nextId = services.length ? Math.max(...services.map((s) => s.id)) + 1 : 1;
    setServices((prev) => [
      ...prev,
      {
        id: nextId,
        name: newName.trim(),
        desc: newDesc.trim() || "خدمة جديدة",
        duration: newDuration.trim() || "20 دقيقة",
        enabled: true,
      },
    ]);
    // Add to price grid with empty prices
    setPriceGrid((prev) => [
      ...prev,
      { service: newName.trim(), prices: vehicleTypes.map(() => "") },
    ]);
    setAddServiceOpen(false);
    flash();
  };

  const openAddOption = () => {
    setNewOptionName("");
    setNewOptionPrice("");
    setOptionError("");
    setAddOptionOpen(true);
  };

  const submitOption = () => {
    if (!newOptionName.trim()) {
      setOptionError("اكتب اسم الخيار أولاً");
      return;
    }
    if (!newOptionPrice.trim()) {
      setOptionError("اكتب سعر الخيار");
      return;
    }
    const nextId = options.length ? Math.max(...options.map((o) => o.id)) + 1 : 1;
    setOptions((prev) => [
      ...prev,
      {
        id: nextId,
        name: newOptionName.trim(),
        price: newOptionPrice.trim(),
        enabled: true,
      },
    ]);
    setAddOptionOpen(false);
    flash();
  };

  return (
    <div className="space-y-5">
      {/* Services */}
      <div className="rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">الخدمات</h2>
        <div className="mt-4 divide-y divide-[#F4F8F8]">
          {services.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-4">
              <div className="flex items-center gap-4">
                <Toggle checked={s.enabled} onChange={() => toggleService(s.id)} />
                <div>
                  <p className={`text-base font-semibold ${s.enabled ? "text-ghasla-text-heading" : "text-ghasla-text-secondary"}`}>
                    {s.name}
                  </p>
                  <p className="mt-0.5 text-[13px] text-ghasla-text-secondary">{s.desc} · {s.duration}</p>
                </div>
              </div>
              <button type="button" className="cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline">
                تعديل
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={openAddService}
          className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#C9DEDE] py-3 text-sm font-medium text-ghasla-text-link transition-colors hover:border-[#0D6E6E]"
        >
          <i className="ri-add-line" />
          إضافة خدمة
        </button>

        {/* Inline add service form */}
        {addServiceOpen && (
          <div className="mt-4 rounded-[20px] border border-[#E4EFEF] bg-[#F0FDFD] p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-ghasla-text-heading">إضافة خدمة جديدة</p>
              <button
                type="button"
                onClick={() => setAddServiceOpen(false)}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary hover:bg-[#E4EFEF]"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ghasla-text-primary">اسم الخدمة</label>
                <input
                  className="h-10 w-full rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm outline-none focus:border-[#0D6E6E]"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="مثال: غسل بالبخار"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ghasla-text-primary">وصف الخدمة</label>
                <input
                  className="h-10 w-full rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm outline-none focus:border-[#0D6E6E]"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="وصف مختصر"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ghasla-text-primary">المدة</label>
                <input
                  className="h-10 w-full rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm outline-none focus:border-[#0D6E6E]"
                  value={newDuration}
                  onChange={(e) => setNewDuration(e.target.value)}
                  placeholder="مثال: 30 دقيقة"
                />
              </div>
            </div>
            {formError && <p className="mt-2 text-xs font-medium text-[#E74C3C]">{formError}</p>}
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={submitService}
                className="flex-1 cursor-pointer rounded-xl bg-[#0D6E6E] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
              >
                إضافة الخدمة
              </button>
              <button
                type="button"
                onClick={() => setAddServiceOpen(false)}
                className="cursor-pointer rounded-xl border border-[#E4EFEF] bg-white px-4 py-2.5 text-sm font-medium text-ghasla-text-primary hover:bg-[#F0FDFD]"
              >
                إلغاء
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Price grid */}
      <div className="rounded-[20px] bg-white shadow-card">
        <div className="p-6 pb-0">
          <h2 className="text-lg font-semibold text-ghasla-text-heading">الأسعار حسب نوع السيارة</h2>
          <p className="mt-1 text-[13px] text-ghasla-text-secondary">حدد سعر كل خدمة لكل نوع سيارة</p>
        </div>
        <div className="overflow-x-auto p-6">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="text-xs font-semibold text-ghasla-text-secondary">
                <th className="h-11 px-3 text-right">الخدمة</th>
                {vehicleTypes.map((v) => (
                  <th key={v} className="h-11 px-3 text-center">{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {priceGrid.map((row, ri) => (
                <tr key={row.service} className="border-t border-[#F4F8F8]">
                  <td className="h-12 px-3 text-ghasla-text-primary">{row.service}</td>
                  {row.prices.map((price, ci) => (
                    <td key={ci} className="h-12 px-2 text-center">
                      <input
                        className="h-9 w-20 rounded-lg border border-[#E4EFEF] bg-[#F0FDFD] text-center text-sm text-ghasla-text-heading outline-none focus:border-[#0D6E6E]"
                        value={price}
                        placeholder="-"
                        onChange={(e) => setPrice(ri, ci, e.target.value)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Options */}
      <div className="rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">خيارات إضافية</h2>
        <div className="mt-4 space-y-3">
          {options.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded-xl bg-[#F0FDFD] px-4 py-3">
              <div className="flex items-center gap-3">
                <Toggle size="sm" checked={o.enabled} onChange={() => toggleOption(o.id)} />
                <span className={`text-sm ${o.enabled ? "text-ghasla-text-heading" : "text-ghasla-text-secondary"}`}>{o.name}</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-ghasla-text-primary">
                <input
                  className="h-8 w-16 rounded-lg border border-[#E4EFEF] bg-white text-center text-sm outline-none focus:border-[#0D6E6E]"
                  value={o.price}
                  readOnly
                />
                <span>ر.س</span>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={openAddOption}
          className="mt-4 cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline"
        >
          + إضافة خيار
        </button>

        {/* Inline add option form */}
        {addOptionOpen && (
          <div className="mt-4 rounded-[20px] border border-[#E4EFEF] bg-[#F0FDFD] p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-ghasla-text-heading">إضافة خيار جديد</p>
              <button
                type="button"
                onClick={() => setAddOptionOpen(false)}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary hover:bg-[#E4EFEF]"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ghasla-text-primary">اسم الخيار</label>
                <input
                  className="h-10 w-full rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm outline-none focus:border-[#0D6E6E]"
                  value={newOptionName}
                  onChange={(e) => setNewOptionName(e.target.value)}
                  placeholder="مثال: تلميع الفوانيس"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ghasla-text-primary">السعر (ر.س)</label>
                <input
                  type="number"
                  className="h-10 w-full rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm outline-none focus:border-[#0D6E6E]"
                  value={newOptionPrice}
                  onChange={(e) => setNewOptionPrice(e.target.value)}
                  placeholder="مثال: 15"
                />
              </div>
            </div>
            {optionError && <p className="mt-2 text-xs font-medium text-[#E74C3C]">{optionError}</p>}
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={submitOption}
                className="flex-1 cursor-pointer rounded-xl bg-[#0D6E6E] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
              >
                إضافة الخيار
              </button>
              <button
                type="button"
                onClick={() => setAddOptionOpen(false)}
                className="cursor-pointer rounded-xl border border-[#E4EFEF] bg-white px-4 py-2.5 text-sm font-medium text-ghasla-text-primary hover:bg-[#F0FDFD]"
              >
                إلغاء
              </button>
            </div>
          </div>
        )}
      </div>

      {saved && (
        <div className="fixed bottom-6 left-6 flex items-center gap-2 rounded-xl bg-[#E8F9F0] px-4 py-2.5 text-sm font-medium text-[#27AE60] shadow-card">
          <i className="ri-checkbox-circle-fill" />
          تم الحفظ ✓
        </div>
      )}
    </div>
  );
}